import React, { useMemo, type ReactNode, type JSX } from 'react';
import { type SelectListItem } from '../../typings/viewModels/selectListItemViewModel';
import useTheme from '../../contexts/useTheme';
import cx from 'classnames';
import UnstyledLink from '../UnstyledLink/UnstyledLink';
import { type LinkWrapper } from '@resources/js/components/LinkWrapper/LinkWrapper';

export const separator = {
  value: 'separator',
  label: '',
};

export type TabbedSelectOption<
  T = string | number,
  L extends NonNullable<ReactNode> = string,
> = Omit<SelectListItem<T>, 'label'> & {
  label: L;
  isDisabled?: boolean;
  isHighlighted?: boolean;
  isExternalLink?: boolean;
  badgeLabel?: string | null;
  badgeLabelStyles?: string[];
  transformLabel?: (option: string) => JSX.Element;
  isVertical?: boolean;
};

type Props<OptionType> = {
  value: OptionType | null;
  options: OptionType[];
  onChange?: (option: OptionType) => void;
  isDisabled?: boolean;
  style?: TabbedSelectStyle;
  backgroundColorLevel?: 0 | 1 | 2;
  LinkWrapper?: LinkWrapper;
  OptionWrapper?: ({
    option,
    children,
  }: {
    option: OptionType;
    children: ReactNode;
  }) => JSX.Element | null;
  className?: string;
  wrapperInheritsProps?: boolean;
  transformLabel?: (option: OptionType) => JSX.Element;
  isVertical?: boolean;
  badgeLabelStyle?: React.CSSProperties;
  dynamicQueryParamsToIgnore?: string[];
  /**
   * When true, every tab is forced to an equal width, so the layout does not
   * shift when a single tab's content changes size
   */
  equalWidthTabs?: boolean;
};

export type TabbedSelectStyle =
  | 'default'
  | 'bubble'
  | 'rounded-square'
  | 'v2'
  | 'v3'
  | 'v3-slim';

function DefaultOptionWrapper({
  children,
}: {
  children: ReactNode;
} & Record<string, unknown>) {
  return <>{children}</>;
}

function TabbedSelect<
  OptionType extends TabbedSelectOption<
    unknown,
    string | NonNullable<ReactNode>
  >,
>({
  value,
  options,
  onChange = () => null,
  isDisabled = false,
  style,
  backgroundColorLevel = 0,
  LinkWrapper,
  OptionWrapper = DefaultOptionWrapper,
  className,
  wrapperInheritsProps,
  transformLabel,
  isVertical = false,
  badgeLabelStyle,
  dynamicQueryParamsToIgnore,
  equalWidthTabs = false,
}: Props<OptionType>): JSX.Element {
  const theme = useTheme();
  style = style ?? theme.tabbedSelectStyle;

  return (
    <div
      className={`tabbed-select tabbed-select--${style} ${
        backgroundColorLevel
          ? `tabbed-select--level-${backgroundColorLevel}`
          : ''
      } ${options.length <= 3 && !isVertical ? 'tabbed-select--short' : ''} ${
        className ? className : ''
      } ${
        isVertical
          ? `tabbed-select--vertical tabbed-select--${style}-vertical`
          : ''
      }${equalWidthTabs ? ' tabbed-select--equal-width-tabs' : ''}`}
    >
      {options.map((option, index) =>
        option === separator ? (
          <div key={index} className={`tabbed-select__separator--${style}`} />
        ) : (
          <TabbedSelectOption
            key={index}
            option={option}
            value={value}
            onChange={onChange}
            style={style}
            isDisabled={isDisabled}
            LinkWrapper={LinkWrapper}
            OptionWrapper={OptionWrapper}
            wrapperInheritsProps={wrapperInheritsProps}
            transformLabel={transformLabel}
            isVertical={isVertical}
            badgeLabelStyle={badgeLabelStyle}
            dynamicQueryParamsToIgnore={dynamicQueryParamsToIgnore}
          />
        ),
      )}
    </div>
  );
}

function TabbedSelectOption<
  OptionType extends TabbedSelectOption<
    unknown,
    NonNullable<ReactNode> | string
  >,
>({
  option,
  value,
  onChange = () => null,
  style,
  isDisabled,
  LinkWrapper,
  OptionWrapper = DefaultOptionWrapper,
  wrapperInheritsProps,
  transformLabel,
  isVertical = false,
  badgeLabelStyle,
  dynamicQueryParamsToIgnore,
}: Pick<
  Props<OptionType>,
  | 'value'
  | 'onChange'
  | 'style'
  | 'isDisabled'
  | 'LinkWrapper'
  | 'OptionWrapper'
  | 'wrapperInheritsProps'
  | 'transformLabel'
  | 'isVertical'
  | 'badgeLabelStyle'
  | 'dynamicQueryParamsToIgnore'
> & {
  option: OptionType;
}) {
  const optionIsDisabled = isDisabled || option.isDisabled;

  const Wrapper = useMemo(
    () =>
      function Wrapper({
        children,
        ...rest
      }: { children: ReactNode } & Record<string, unknown>) {
        return (
          <OptionWrapper option={option} {...rest}>
            {children}
          </OptionWrapper>
        );
      },
    [OptionWrapper, option],
  );

  const label = useMemo(
    () => (transformLabel ? transformLabel(option) : option.label),
    [option, transformLabel],
  );

  const children = (
    <>
      <span className={'tabbed-select__bullet-point'}>•</span>
      {option.iconUrl?.length && (
        <img
          className={'tabbed-select__tab-icon'}
          src={option.iconUrl}
          alt={''}
        />
      )}
      <span
        className={cx({
          'tabbed-select__tab-label': true,
          'tabbed-select__tab-label--selected': option.value === value?.value,
        })}
      >
        {label}
        {option.badgeLabel && (
          <div
            className={cx(
              'tabbed-select__tab-badge-label',
              option.badgeLabelStyles?.map(
                (style) => `tabbed-select__tab-badge-label--${style}`,
              ),
            )}
            style={badgeLabelStyle}
          >
            {option.badgeLabel}
          </div>
        )}
      </span>
      {option.isExternalLink && <i className={'zmdi zmdi-open-in-new'} />}
    </>
  );

  const className = `tabbed-select__tab tabbed-select__tab--${style} ${
    option.value === value?.value
      ? `tabbed-select__tab--selected tabbed-select__tab--${style}-selected`
      : ''
  } ${
    option.isHighlighted
      ? `tabbed-select__tab--highlighted tabbed-select__tab--${style}-highlighted`
      : ''
  } ${
    isVertical
      ? `tabbed-selected__tab--vertical tabbed-select__tab--${style}-vertical`
      : ''
  }`;

  const forwardedHref =
    wrapperInheritsProps && option.url ? option.url : undefined;
  const forwardedClassName =
    wrapperInheritsProps && option.url ? className : undefined;
  const forwardedDynamicQueryParamsToIgnore =
    wrapperInheritsProps && option.url ? dynamicQueryParamsToIgnore : undefined;
  const forwardedLinkWrapper = wrapperInheritsProps ? LinkWrapper : undefined;

  if (option.url) {
    return (
      <Wrapper
        href={forwardedHref}
        className={forwardedClassName}
        LinkWrapper={forwardedLinkWrapper}
        dynamicQueryParamsToIgnore={forwardedDynamicQueryParamsToIgnore}
      >
        {wrapperInheritsProps ? (
          children
        ) : (
          <UnstyledLink
            href={option.url}
            disableDefaultLinkUnstyling={true}
            className={className}
          >
            {children}
          </UnstyledLink>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper
      href={forwardedHref}
      className={forwardedClassName}
      dynamicQueryParamsToIgnore={forwardedDynamicQueryParamsToIgnore}
    >
      <button
        className={className}
        onClick={
          option.url
            ? undefined
            : () =>
                !optionIsDisabled &&
                option.value !== value?.value &&
                onChange(option)
        }
        disabled={optionIsDisabled}
      >
        {children}
      </button>
    </Wrapper>
  );
}

export default TabbedSelect;
